/**
 * A re-check (PLAN §6, Re-check): only the car's open items, Must fix first, each re-graded with
 * the §3.4 options, and any line the inspector adds — a fault a driver reported under 27.2, or a
 * spot check. A new Must fix added here needs its photo like any other.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ChecklistLine, OpenItem } from '@/lib/inspections';

interface Props {
    openItems: readonly OpenItem[];
    extraItems: readonly string[];
    lines: readonly ChecklistLine[];
    renderRow: (item: string, open: OpenItem | null) => React.ReactNode;
    onAdd: (item: string) => void;
}

export default function RecheckList({ openItems, extraItems, lines, renderRow, onAdd }: Props) {
    const [choice, setChoice] = useState<string>('');
    const listed = new Set([...openItems.map((o) => o.item), ...extraItems]);
    const addable = lines.filter((l) => !listed.has(l.key));
    return (
        <div className="space-y-4">
            {openItems.length > 0 ? (
                <div className="space-y-2">
                    {openItems.map((o) => (
                        <div key={o.item}>{renderRow(o.item, o)}</div>
                    ))}
                </div>
            ) : (
                <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                    Nothing is open on this car. Add the line you are checking below.
                </p>
            )}

            {extraItems.length > 0 && (
                <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">Added lines</h3>
                    {extraItems.map((item) => (
                        <div key={item}>{renderRow(item, null)}</div>
                    ))}
                </div>
            )}

            {addable.length > 0 && (
                <div className="flex flex-col gap-2 rounded-xl border bg-muted/30 p-3 sm:flex-row sm:items-center">
                    <Select value={choice} onValueChange={setChoice}>
                        <SelectTrigger className="h-11 sm:w-72" aria-label="Line to add">
                            <SelectValue placeholder="Add a line to check" />
                        </SelectTrigger>
                        <SelectContent>
                            {addable.map((l) => (
                                <SelectItem key={l.key} value={l.key}>
                                    {l.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        type="button"
                        variant="outline"
                        className="h-11"
                        disabled={!choice}
                        onClick={() => {
                            onAdd(choice);
                            setChoice('');
                        }}
                    >
                        <Plus aria-hidden />
                        Add line
                    </Button>
                </div>
            )}
        </div>
    );
}
